// Shared state + logic for the photo validation dialog. Both upload entry
// points (the dropzone `InputFileUploadSingle` and the webcam
// `InputFileUploadSingleWithWebcam`) use this hook so the interception logic
// lives in a single, reusable place.
import { useCallback, useState } from 'react';

import { IPhotoValidationResult } from '../constants/photoValidation';
import {
  isWarnOnlyWithIssues,
  parsePhotoValidationResult,
} from '../utils/photoValidation';

interface IPhotoValidationState {
  isOpen: boolean;
  result: IPhotoValidationResult | null;
  previewUrl: string | null;
  // When true, closing the dialog revokes blob: previewUrl (used for rejected
  // uploads). Warn-only previews are owned by the upload component / remote URL.
  ownsPreview: boolean;
}

const INITIAL_STATE: IPhotoValidationState = {
  isOpen: false,
  result: null,
  previewUrl: null,
  ownsPreview: false,
};

const revokePreview = (previewUrl: string | null) => {
  if (previewUrl && previewUrl.startsWith('blob:')) {
    URL.revokeObjectURL(previewUrl);
  }
};

export const usePhotoValidation = () => {
  const [state, setState] = useState<IPhotoValidationState>(INITIAL_STATE);

  const close = useCallback(() => {
    setState((previous) => {
      if (previous.ownsPreview) {
        revokePreview(previous.previewUrl);
      }
      return INITIAL_STATE;
    });
  }, []);

  /**
   * Attempts to handle a failed upload as a photo validation (HTTP 422) result.
   * Pass the raw response (XHR response string or parsed axios body) and a local
   * preview URL for the rejected image.
   *
   * Returns true when the failure was a validation error and the dialog was
   * opened, allowing callers to skip their generic error handling. Returns false
   * otherwise (and does not take ownership of `previewUrl`).
   */
  const handlePotentialValidationError = useCallback(
    (rawResponse: unknown, previewUrl: string | null): boolean => {
      const result = parsePhotoValidationResult(rawResponse);
      if (!result) {
        return false;
      }

      setState((previous) => {
        if (previous.ownsPreview) {
          revokePreview(previous.previewUrl);
        }
        return { isOpen: true, result, previewUrl, ownsPreview: true };
      });
      return true;
    },
    [],
  );

  /**
   * Opens the validation dialog for a successful upload that returned WARN_ONLY
   * with issues. The photo remains accepted; callers should keep their uploaded
   * field value. Prefer a local blob: preview URL (same as reject mode) so the
   * modal does not depend on a private S3 location URL. Ownership stays with
   * the upload component (`ownsPreview: false`) because FilePreview also uses
   * that blob.
   *
   * Returns true when the dialog was opened.
   */
  const handlePotentialValidationWarnings = useCallback(
    (rawResponse: unknown, previewUrl: string | null): boolean => {
      const result = parsePhotoValidationResult(rawResponse);
      if (!isWarnOnlyWithIssues(result)) {
        return false;
      }

      setState((previous) => {
        if (previous.ownsPreview) {
          revokePreview(previous.previewUrl);
        }
        return { isOpen: true, result, previewUrl, ownsPreview: false };
      });
      return true;
    },
    [],
  );

  return {
    isOpen: state.isOpen,
    result: state.result,
    previewUrl: state.previewUrl,
    handlePotentialValidationError,
    handlePotentialValidationWarnings,
    close,
  };
};

export default usePhotoValidation;
